const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const ApiResponseUnmapper = require('@/libraries/DigiTickets/Api/ApiResponseUnmapper');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const SessionEvent = require('./SessionEvent');

class SessionEventRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param {ApiClient} apiClient
     * @param {ApiRequestParams} apiRequestParams
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        apiClient,
        apiRequestParams
    ) {
        super(dataStore, hydrator, syncStateManager);

        this.apiClient = apiClient;
        this.apiRequestParams = apiRequestParams;

        /**
         * If you use the getEventName() method this object will be populated with the names of eventIDs
         * to prevent constant loading from IndexedDB.
         *
         * @type {Object.<number, string|boolean>}
         */
        this.eventNameCache = {};
    }

    getEntityClass() {
        return SessionEvent;
    }

    getEntityName() {
        return 'Event';
    }

    getTableName() {
        return DataStoreNames.SESSION_EVENTS;
    }

    getResource() {
        return null;
    }

    loadFromApi(branchID = null) {
        return this.apiClient
            .request(
                'GET',
                'events',
                {
                    branchID, // Send the requested branchID to the API so it is used instead of any default.
                    // The events endpoint only returns events that have sessions, unless you use this flag:
                    includeEmpty: 1,
                    fields: [
                        'eventCatID',
                        'imageUrl',
                        'name',
                        'navOrder',
                        'sessionType',
                        'staffSalesCutoffPointAfterSessionStart',
                        'staffSalesCutoffTime',
                        'status',
                        'amberThreshold',
                        'yellowThreshold',
                        'redThreshold',
                    ].join(','),
                    ...this.apiRequestParams.defaultMappers(),
                    resolve: 'ticketcats',
                }
            )
            .then((response) => ApiResponseUnmapper.transformApiResponse(response.data, 'Ticket'))
            .then((data) => {
                return this.hydrator.hydrateArray(
                    data,
                    () => new (this.getEntityClass())()
                );
            });
    }

    /**
     * @param {number} eventID
     *
     * @return {Promise<string>}
     */
    async getEventName(eventID) {
        // Check for a cached name for the event.
        if (this.eventNameCache.hasOwnProperty(eventID)) {
            // If `false` was cached that means we already checked and the event does not exist.
            if (this.eventNameCache[eventID] === false) {
                throw new Error(`Event ${eventID} not found.`);
            }

            return this.eventNameCache[eventID];
        }

        // No cached name for this event. Check IndexedDB.
        return this.find(eventID)
            .then((event) => {
                this.eventNameCache[eventID] = event.name;

                return event.name;
            })
            .catch((e) => {
                this.eventNameCache[eventID] = false;

                throw e;
            });
    }

    /**
     * Sets the 'item' property of each line in the given array of order/cart lines, where that item can be
     * found in the local store.
     *
     * @param {Ticket[]} tickets
     *
     * @return {Promise<Ticket[]>}
     */
    async attachEventsToTickets(tickets) {
        const eventsToLoad = {};
        const ticketsWithEvents = tickets.filter((t) => {
            if (t.eventID) {
                eventsToLoad[t.eventID] = true;
                return true;
            }
            return false;
        });

        const events = {};
        await Promise.all(
            Object
                .keys(eventsToLoad)
                .map((eventID) => {
                    return this.find(eventID)
                        .then((event) => {
                            events[eventID] = event;
                        })
                        .catch(() => {
                            events[eventID] = null;
                        });
                })
        );

        ticketsWithEvents.forEach((ticket) => {
            ticket.event = events[ticket.eventID];
            if (!ticket.event) {
                console.error(`Failed to load event ${ticket.eventID} for ticket ${ticket.ID}.`);
            }
        });

        return tickets;
    }
}

module.exports = SessionEventRepository;
